import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import type { RevelationContent } from '../../types/salesPage';
interface RevelationSectionProps {
  content: RevelationContent;
}
export function RevelationSection({
  content
}: RevelationSectionProps) {
  return <section aria-labelledby="revelation-title" className="border-t border-white/[0.07] bg-carbon px-5 py-14 sm:px-6 md:py-20" data-id="element-218" data-mp="1sj7hc0o2a21l">
      <div className="mx-auto max-w-5xl" data-id="element-219" data-mp="1eioct3it1e46">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14" data-id="element-220" data-mp="1ilbbnodw7zav">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-70px'
        }} transition={{
          duration: 0.55,
          ease: 'easeOut'
        }} data-id="element-221" data-mp="14ls3wwozupz3">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-gold md:text-[10px]" data-id="element-222" data-mp="2z0uku5zz8zqe">
              {content.eyebrow}
            </p>
            <h2 id="revelation-title" className="mt-3 font-serif text-[24px] font-bold leading-tight text-parchment sm:text-[30px] md:text-[36px]" data-id="element-223" data-mp="1befkgcljcgok">
              {content.title}
            </h2>

            <blockquote className="mt-6 rounded-sm border border-gold/20 bg-gold/[0.05] px-5 py-5 text-center" data-id="element-224" data-mp="1k6zet0usnz3l">
              <p className="font-serif text-[18px] italic leading-[1.55] text-parchment md:text-[21px]" data-id="element-225" data-mp="1g6ffbqmr0jea">
                {content.verse}
              </p>
              <cite className="mt-3 block font-mono text-[9.5px] not-italic uppercase tracking-[0.2em] text-gold" data-id="element-226" data-mp="nggobpozuc6z">
                {content.verseRef}
              </cite>
            </blockquote>

            {content.paragraphs.map(paragraph => <p key={paragraph} className="mt-4 text-[14.5px] font-light leading-[1.75] text-smoke md:text-[15.5px]" data-id="element-227" data-mp="wfliykxfpldv">
                {paragraph}
              </p>)}
          </motion.div>

          <motion.figure initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-70px'
        }} transition={{
          duration: 0.55,
          delay: 0.08,
          ease: 'easeOut'
        }} data-id="element-228" data-mp="15j5uvo8su4dm">
            <div className="overflow-hidden rounded-sm border border-white/10" data-id="element-229" data-mp="1ekdgp6u4pcqt">
              <img src={content.image.src} alt={content.image.alt} loading="lazy" decoding="async" className="h-56 w-full object-cover sm:h-72 lg:h-[380px]" data-id="element-230" data-mp="1jhdnxl2pslji" />
            </div>
          </motion.figure>
        </div>

        <div className="mt-10 grid gap-4 md:mt-12 md:grid-cols-2 md:gap-5" data-id="element-231" data-mp="vrjc96u4t30u">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="rounded-sm border border-white/10 bg-panel/60 px-5 py-5" data-id="element-232" data-mp="10kulfemf1vvx">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ash" data-id="element-233" data-mp="jh2n4xz0abd">
              {content.contrast.oldWay.label}
            </p>
            <ul className="mt-4 space-y-2.5" data-id="element-234" data-mp="3mqxffjm8uub7">
              {content.contrast.oldWay.items.map(item => <li key={item} className="flex gap-2.5 text-[14px] font-light text-ash line-through decoration-white/20" data-id="element-235" data-mp="3iq57ctp3yvgi">
                  <X aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[#C65C53]" data-id="element-236" data-mp="3drkdrd4d3a11" />
                  {item}
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.08,
          ease: 'easeOut'
        }} className="rounded-sm border border-gold/35 bg-gold/[0.05] px-5 py-5" data-id="element-237" data-mp="341r6gob95h2l">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-gold-soft" data-id="element-238" data-mp="2q1q1oxrv9phu">
              {content.contrast.newWay.label}
            </p>
            <ul className="mt-4 space-y-2.5" data-id="element-239" data-mp="xzn4tlnqax3g">
              {content.contrast.newWay.items.map(item => <li key={item} className="flex gap-2.5 text-[14px] font-light text-ivory/90" data-id="element-240" data-mp="k6xjzkjxry7x">
                  <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-gold" data-id="element-241" data-mp="p61po17njdti" />
                  {item}
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}